import {
  Checkbox,
  TableCellProps, Tooltip, Typography
} from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import React, { MouseEvent } from 'react';
// @ts-expect-error: missing types
import { ButtonBase } from '@one-thd/sui-atomic-components/dist/button/ButtonBase';
// @ts-expect-error: missing types
import useTable from '@one-thd/sui-atomic-components/dist/private/components/table/useTable';
// @ts-expect-error: missing types
import useTableHead from '@one-thd/sui-atomic-components/dist/private/components/table/useTableHead';
// @ts-expect-error: missing types
import { SortAsc } from '@one-thd/sui-atomic-components/dist/private/icons/SortAsc';
import PropTypes from 'prop-types';
import { Info } from '@one-thd/sui-icons';
import { DataGridCell } from './DataGridCell';
import { DataGridData, SortBy, TableColumn } from './types';
import { HeaderPopover } from './HeaderPopover';

export type DataGridHeaderCellProps<TData extends DataGridData> = Omit<TableCellProps, 'ref'> & {
  column: TableColumn<TData>;
  isIndeterminated?: boolean,
  isChecked?: boolean | undefined,
  onSort?: (event: MouseEvent, column: TableColumn<TData>, sortBy: SortBy | null) => void;
  onColumnCheck?:(event:React.ChangeEvent, isChecked:boolean)=>void;
};

export const DataGridHeaderCell = <TData extends DataGridData, >(props: DataGridHeaderCellProps<TData>) => {
  const {
    column, onColumnCheck, isIndeterminated, isChecked, onSort, ...other
  } = props;
  const {
    sortBy, renderHeader, title, colSpan, align, enableSorting, disabled
  } = column;

  const table = useTable();
  const tableHead = useTableHead();
  const density = tableHead?.density || table?.density || 'normal';

  const cellClasses = classNames('sui-flex sui-items-center', {
    'sui-justify-center': align === 'center',
    'sui-justify-start': align === 'left',
    'sui-justify-end': align === 'right',
    [`${classNames('sui-group sui-w-full sui-h-full sui-px-4', {
      'sui-py-1': density === 'tight',
      'sui-py-2': density === 'normal' || !density,
      'sui-py-4': density === 'loose'
    })}`]: enableSorting,
  });

  const iconClasses = classNames('sui-flex sui-transition-transform sui-duration-250 sui-fill-primary sui-px-1', {
    'sui-rotate-0': sortBy === 'asc',
    'sui-rotate-180': sortBy === 'desc',
    'sui-opacity-0 group-hover:sui-opacity-100 group-focus-visible:sui-opacity-100': !enableSorting,
    'sui-text-inactive': disabled,
    'sui-text-primary': !disabled,
  });

  // SORTING
  const handleSortClick = (event: MouseEvent<HTMLDivElement>) => {
    // Prevents click firing when clicking checkbox
    if ((event.target as HTMLDivElement).role === 'checkbox') return;

    if (enableSorting) {
      onSort?.(event, column, sortBy || null);
    }
  };

  // CHECKBOX
  const handleColumnCheck = (e: React.ChangeEvent) => {
    if (onColumnCheck) {
      onColumnCheck(e, isChecked as boolean);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-shadow
  const Component = (props: any) => {
    return enableSorting ? (
      <ButtonBase
        data-testid={`table-header-${column.key}-button`}
        unstyled
        role="button"
        {...props}
        onClick={handleSortClick}
        disabled={disabled}
      />
    ) : (
      <div data-testid={`table-header-${column.key}`} {...props} />
    );
  };

  Component.displayName = 'SortableHeaderCell';

  return (
    <DataGridCell
      {...other}
      colSpan={colSpan}
      // @ts-expect-error: missing types
      sortDirection={sortBy ?? undefined}
      padding={enableSorting ? 'none' : other.padding}
      className={classNames({
        'hover:sui-bg-strong focus-visible:sui-bg-button-focus focus-visible:sui-outline-none': enableSorting
      }, other.className)}
    >
      <Component className={cellClasses}>
        <div className="sui-flex sui-items-center sui-gap-x-3">
          {/* Checkbox */}
          {column?.isCheckable && (
            <Checkbox
              checked={isChecked}
              onChange={handleColumnCheck}
              indeterminate={isIndeterminated}
              disabled={disabled}
              aria-label="Select all rows"
              inputAttributes={{
                // @ts-expect-error: missing types
                'data-testid': `${column.key}-checkbox`
              }}
            />
          )}

          {/* Header */}
          {renderHeader ? (
            renderHeader(
              column, {
                isChecked,
                isIndeterminated,
                onColumnCheck: handleColumnCheck,
                onSort: handleSortClick
              })
          ) : (
            <Typography variant="body-base" height="tight" weight="bold" component="span" color={disabled ? 'inactive' : 'primary'}>
              {title}
            </Typography>
          )}

          {/* Tooltip */}
          {column?.tooltip && (
            <Tooltip offset="loose" title={column.tooltip}>
              <div className="sui-flex sui-px-1 sui-fill-primary">
                <Info size="small" />
              </div>
            </Tooltip>
          )}

          {/* Popover */}
          {column?.popover && (
            <HeaderPopover popover={column.popover} />
          )}

          {/* Sort */}
          {!enableSorting || !sortBy ? null : (
            <div className={iconClasses}>
              <SortAsc size="small" color="current" />
            </div>
          )}
        </div>
      </Component>
    </DataGridCell>
  );
};

DataGridHeaderCell.displayName = 'DataGridHeaderCell';

DataGridHeaderCell.propTypes = {
  column: PropTypes.shape({
    title: PropTypes.string,
    key: PropTypes.string,
    renderHeader: PropTypes.func,
    sortBy: PropTypes.oneOf(['asc', 'desc', null]),
    colSpan: PropTypes.number,
    align: PropTypes.oneOf(['left', 'right', 'center', 'justify']),
    enableSorting: PropTypes.bool,
    isNumber: PropTypes.bool,
    isCheckable: PropTypes.bool,
    renderCell: PropTypes.func,
    dataIndex: PropTypes.string,
    sticky: PropTypes.bool,
    tooltip: PropTypes.string,
    popover: PropTypes.shape({
      title: PropTypes.string.isRequired,
      content: PropTypes.node,
      placement: PropTypes.oneOf(['top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end', 'left', 'left-start', 'left-end', 'right', 'right-start', 'right-end']),
    }),
  }).isRequired,
  component: PropTypes.elementType,
  currentData: PropTypes.shape({}),
  isIndeterminated: PropTypes.bool,
  isChecked: PropTypes.bool,
  onsort: PropTypes.func,
  onColumnCheck: PropTypes.func,
// Typescript overrides types for props when types include a generic interface and propTypes are passed
// eslint-disable-next-line @typescript-eslint/ban-types
} as {};
